import {
  DOMINO_SEAT_OPTIONS,
  type DominoLobbyDto,
  type DominoViewDto,
  isVipActive,
  type LiveStreamDto,
} from '@spin/shared';
import { useCallback, useEffect, useState } from 'react';
import { http } from '../api/http';
import { Avatar, Spinner, useBackButton } from '../components/common';
import { DominoIcon } from '../components/icons';
import { reportError, useT } from '../i18n';
import { useSession } from '../store/session';
import { useUi } from '../store/ui';

function useInterval(load: () => void, ms: number): void {
  useEffect(() => {
    load();
    const id = window.setInterval(load, ms);
    return () => window.clearInterval(id);
  }, [load, ms]);
}

/**
 * Watch (the portal's live tile): live streams and domino rooms in two tabs, "+" to go live (VIP), as in the
 * reference's "Canlı / Domino" page.
 */
export function Watch({ tab: initial = 'live' }: { tab?: 'live' | 'domino' }) {
  const t = useT();
  const { go, back, me, config } = useSession();
  const [tab, setTabState] = useState(initial);
  const setTab = (next: 'live' | 'domino') => {
    setTabState(next);
    useSession.getState().replace({ name: 'watch', tab: next });
  };
  const [streams, setStreams] = useState<LiveStreamDto[] | null>(null);
  const [lobby, setLobby] = useState<DominoLobbyDto | null>(null);
  useBackButton(back);

  const loadStreams = useCallback(() => {
    if (config?.live.enabled) http.get<LiveStreamDto[]>('/live').then(setStreams).catch(reportError);
    else setStreams([]);
  }, [config?.live.enabled]);
  const loadLobby = useCallback(() => {
    if (config?.domino) http.get<DominoLobbyDto>('/domino').then(setLobby).catch(reportError);
  }, [config?.domino]);
  useInterval(loadStreams, 10_000);
  useInterval(loadLobby, 5_000);

  const locked = Boolean(config?.live.vipOnly && !isVipActive(me?.vipUntil ?? null));
  const goLive = () => {
    if (!config?.live.enabled) {
      useUi.getState().toast(t('errors.LIVE_DISABLED'), 'info');
      return;
    }
    if (locked) {
      useUi.getState().toast(t('live.vip_only'), 'info');
      return;
    }
    go({ name: 'liveStart' });
  };

  const open = lobby?.rooms.length ?? 0;
  return (
    <div className="portal watch-page" data-testid="watch">
      <div className="portal-page">
        <div className="watch-bar">
          <button className="watch-back" onClick={back} data-testid="watch-back">
            ← {t('watch.back')}
          </button>
          <button
            className="watch-plus"
            onClick={goLive}
            aria-label={t('live.go_live')}
            data-testid="live-go"
          >
            +
          </button>
          <b>{t('watch.live_caps')}</b>
        </div>
        <div className="watch-tabs">
          <button
            className={tab === 'live' ? 'on' : ''}
            onClick={() => setTab('live')}
            data-testid="tab-live"
          >
            {t('watch.live_tab', { count: streams?.length ?? 0 })}
          </button>
          <button
            className={tab === 'domino' ? 'on' : ''}
            onClick={() => setTab('domino')}
            data-testid="tab-domino"
          >
            <DominoIcon /> {t('watch.domino_tab', { count: open })}
          </button>
        </div>
        {tab === 'live' ? (
          !streams ? (
            <Spinner />
          ) : streams.length === 0 ? (
            <div className="watch-empty">
              <span className="watch-cam" aria-hidden>
                🎥
              </span>
              <p>{t('watch.no_streams')}</p>
              <button className="portal-btn purple" onClick={goLive} data-testid="live-first">
                {t('watch.first_stream')}
              </button>
            </div>
          ) : (
            <div className="live-grid" data-testid="live-streams">
              {streams.map((s) => (
                <button
                  key={s.id}
                  className="live-card"
                  onClick={() => go({ name: 'liveRoom', id: s.id })}
                  data-testid={`live-card-${s.id}`}
                >
                  <span className="live-cover">
                    <Avatar name={s.streamer.displayName} photoUrl={s.streamer.photoUrl} size="100%" />
                    <i className="live-badge">LIVE</i>
                    <i className="live-eye">👁 {s.viewers}</i>
                  </span>
                  <b className="ellipsis">{s.streamer.displayName}</b>
                  <small className="ellipsis">{s.title}</small>
                </button>
              ))}
            </div>
          )
        ) : !config?.domino ? (
          <div className="watch-empty">
            <p>{t('errors.DOMINO_DISABLED')}</p>
          </div>
        ) : !lobby ? (
          <Spinner />
        ) : (
          <DominoLobby lobby={lobby} reload={loadLobby} />
        )}
      </div>
    </div>
  );
}

function DominoLobby({ lobby, reload }: { lobby: DominoLobbyDto; reload: () => void }) {
  const t = useT();
  const go = useSession((s) => s.go);
  const [seats, setSeats] = useState<number>(2);
  const [stake, setStake] = useState(0);
  const [busy, setBusy] = useState(false);
  const stakeLabel = (n: number) => (n ? `❤ ${n}` : t('domino.free'));

  const enter = async (request: () => Promise<DominoViewDto>) => {
    setBusy(true);
    try {
      const room = await request();
      go({ name: 'dominoRoom', id: room.id });
    } catch (err) {
      reportError(err);
      reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="domino-lobby">
      <p className="dark-hint">{t('domino.rules', { target: lobby.target })}</p>
      {lobby.current && (
        <button
          className="portal-btn purple"
          onClick={() => go({ name: 'dominoRoom', id: lobby.current! })}
          data-testid="domino-current"
        >
          🎲 {t('domino.back_to_game')}
        </button>
      )}
      <div className="dark-section">{t('domino.quick')}</div>
      <div className="stake-row">
        {lobby.stakes.map((n) => (
          <button
            key={n}
            className="portal-btn"
            disabled={busy || Boolean(lobby.current)}
            onClick={() => void enter(() => http.post<DominoViewDto>('/domino/quick', { stake: n }))}
            data-testid={`domino-quick-${n}`}
          >
            {stakeLabel(n)}
          </button>
        ))}
      </div>
      <div className="dark-section">{t('domino.create')}</div>
      <div className="portal-card domino-create">
        <div className="choice-row">
          <span>{t('domino.seats')}</span>
          {DOMINO_SEAT_OPTIONS.map((n) => (
            <button key={n} className={seats === n ? 'on' : ''} onClick={() => setSeats(n)}>
              {n}
            </button>
          ))}
        </div>
        <div className="choice-row">
          <span>{t('domino.stake_label')}</span>
          {lobby.stakes.map((n) => (
            <button key={n} className={stake === n ? 'on' : ''} onClick={() => setStake(n)}>
              {stakeLabel(n)}
            </button>
          ))}
        </div>
        <button
          className="portal-btn purple"
          disabled={busy || Boolean(lobby.current)}
          onClick={() => void enter(() => http.post<DominoViewDto>('/domino/rooms', { seats, stake }))}
          data-testid="domino-create"
        >
          {t('domino.create')}
        </button>
      </div>
      <div className="dark-section">{t('domino.open_rooms')}</div>
      {lobby.rooms.length === 0 ? (
        <div className="portal-empty">{t('domino.no_rooms')}</div>
      ) : (
        <div className="invited-list" data-testid="domino-rooms">
          {lobby.rooms.map((room) => (
            <div key={room.id} className="portal-card domino-room">
              <span className="domino-avatars">
                {room.players.map((p) => (
                  <Avatar key={p.userId} name={p.name} photoUrl={p.photoUrl} size={28} />
                ))}
              </span>
              <span className="ellipsis">
                {room.players.length}/{room.seats} · {stakeLabel(room.stake)}
              </span>
              <button
                className="portal-btn green"
                disabled={busy || Boolean(lobby.current)}
                onClick={() => void enter(() => http.post<DominoViewDto>(`/domino/rooms/${room.id}/join`))}
              >
                {t('domino.join')}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
